import { API_URL, WS_URL } from "config";

export type TaskLogEvent = {
  timestamp?: number | null;
  message: string;
};

export type TaskLogsWsMessage =
  | { type: "logs"; events: TaskLogEvent[] }
  | { type: "end"; status?: string }
  | { type: "unavailable"; reason?: string }
  | { type: "pong" };

type MessageListener = (message: TaskLogsWsMessage) => void;
type StatusListener = (connected: boolean) => void;
type AuthRejectedListener = () => void;
type TokenProvider = () => string | null;

const WS_CLOSE_UNAUTHORIZED = 4401;
const WS_CLOSE_FORBIDDEN = 4403;
const WS_CLOSE_NOT_FOUND = 4404;

/**
 * Per-task CloudWatch live-log WebSocket client.
 *
 * Mirrors botsManagementWs but connects to ws/task-logs/<taskId>/ and is driven
 * directly from the Task Details page (only one task is viewed at a time).
 */
class TaskLogsWsManager {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectDelayMs = 2000;
  private disposed = false;
  private authRejected = false;
  private connected = false;
  private token: string | null = null;
  private taskId: number | null = null;
  private getToken: TokenProvider | null = null;
  private messageListeners = new Set<MessageListener>();
  private statusListeners = new Set<StatusListener>();
  private authRejectedListeners = new Set<AuthRejectedListener>();

  subscribe(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    return () => this.messageListeners.delete(listener);
  }

  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  subscribeAuthRejected(listener: AuthRejectedListener): () => void {
    this.authRejectedListeners.add(listener);
    return () => this.authRejectedListeners.delete(listener);
  }

  setTokenProvider(provider: TokenProvider | null) {
    this.getToken = provider;
  }

  isConnected(): boolean {
    return this.connected;
  }

  connect(token: string, taskId: number) {
    if (!token || taskId == null) return;
    if (
      this.token === token &&
      this.taskId === taskId &&
      this.socket?.readyState === WebSocket.OPEN
    ) {
      return;
    }
    this.token = token;
    this.taskId = taskId;
    this.disposed = false;
    this.authRejected = false;
    this.teardownSocket();
    this.openSocket();
  }

  disconnect() {
    this.disposed = true;
    this.authRejected = false;
    this.token = null;
    this.taskId = null;
    this.teardownSocket();
    this.setConnected(false);
  }

  private resolveToken(): string | null {
    const freshToken = this.getToken?.() ?? null;
    return freshToken || this.token;
  }

  private notifyMessage(message: TaskLogsWsMessage) {
    this.messageListeners.forEach((listener) => listener(message));
  }

  private notifyAuthRejected() {
    this.authRejectedListeners.forEach((listener) => listener());
  }

  private setConnected(next: boolean) {
    if (this.connected === next) return;
    this.connected = next;
    this.statusListeners.forEach((listener) => listener(next));
  }

  private teardownSocket() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
      this.socket = null;
    }
  }

  private scheduleReconnect() {
    if (this.disposed || this.authRejected) return;
    this.reconnectTimer = setTimeout(() => this.openSocket(), this.reconnectDelayMs);
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 30000);
  }

  private openSocket() {
    if (this.disposed || this.authRejected) return;

    const token = this.resolveToken();
    if (!token || this.taskId == null) return;

    this.token = token;
    const wsBaseRaw = WS_URL || API_URL.replace(/^http:\/\//, "ws://").replace(/^https:\/\//, "wss://");
    const wsBase = wsBaseRaw.replace(/\/+$/, "");
    const wsUrl = `${wsBase}/ws/task-logs/${this.taskId}/?token=${encodeURIComponent(token)}`;
    this.socket = new WebSocket(wsUrl);
    this.socket.onopen = () => {
      this.setConnected(true);
      this.reconnectDelayMs = 2000;
      this.heartbeatTimer = setInterval(() => {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify({ type: "ping" }));
        }
      }, 5000);
    };
    this.socket.onmessage = (message) => {
      try {
        const payload = JSON.parse(message.data) as TaskLogsWsMessage;
        if (payload?.type) {
          this.notifyMessage(payload);
        }
      } catch {
        return;
      }
    };
    this.socket.onerror = () => {
      this.socket?.close();
    };
    this.socket.onclose = (event) => {
      this.setConnected(false);
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
      if (this.disposed) return;

      // 4404 = local task / task not found — no live logs, do not reconnect.
      if (event.code === WS_CLOSE_NOT_FOUND) {
        this.disposed = true;
        return;
      }

      if (event.code === WS_CLOSE_UNAUTHORIZED || event.code === WS_CLOSE_FORBIDDEN) {
        this.authRejected = true;
        if (event.code === WS_CLOSE_UNAUTHORIZED) {
          this.notifyAuthRejected();
        }
        return;
      }

      this.scheduleReconnect();
    };
  }
}

export const taskLogsWs = new TaskLogsWsManager();
